import {
    Box,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    TextField,
    Typography,
} from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { FlagIcon } from "src/components/iconify/flagIcon";
import CustomLineChart from "src/components/inline-chart/inline-chart-activations-scatter";
import { useUpdateKoBiddingManualMaxCpcMutation } from "src/context/api/activations/api";
import { useCurrentWorkspace } from "src/context/reducers/app-settings";
import countriesJson from "src/utils/countries.json";

export default function ModalActivations({
    open,
    row,
    onClose,
    setModalActivation,
    setLocalPatches,
}: {
    open: boolean;
    row: any;
    onClose: () => void;
    setModalActivation: any;
    setLocalPatches: any;
}) {


    const workspace = useCurrentWorkspace();
    const [updateMaxCpc] = useUpdateKoBiddingManualMaxCpcMutation();

    const rowActivation =
        row?.activations_details?.find?.((d: any) => d.mode === "KO Bidding Manual") || null;

    const [maxCpc, setMaxCpc] = useState<number | "">("");

    useEffect(() => {
        if (rowActivation?.max_cpc !== undefined) {
            setMaxCpc(rowActivation.max_cpc);
        }
    }, [rowActivation]);

    const handleApplyChanges = async () => {
        try {
            if (maxCpc === "" || isNaN(Number(maxCpc))) {
                enqueueSnackbar("Please set a valid Max CPC before applying.", { variant: "warning" });
                return;
            }

            const updated = await updateMaxCpc({
                id: rowActivation?.id,
                workspaceId: workspace?.id,
                max_cpc: Number(maxCpc),
            }).unwrap();

            enqueueSnackbar("Max CPC updated successfully", { variant: "success" });

            setLocalPatches((prev: any) => {
                const existing = prev[row.id] ?? row;

                return {
                    ...prev,
                    [row.id]: {
                        ...existing,
                        activations_details: (existing.activations_details ?? []).map((d: any) =>
                            d.id === rowActivation.id
                                ? { ...d, max_cpc: updated.new_max_cpc }
                                : d
                        )
                    }
                };
            });


            onClose();
        } catch (err) {
            enqueueSnackbar("Failed to update Max CPC", { variant: "error" });
        }
    };


    return (
        <Dialog
            open={open && !!row}
            onClose={onClose}
            maxWidth="xl"
            fullWidth
            PaperProps={{
                sx: {
                    backgroundColor: "#161C24",
                    borderRadius: 3,
                    py: 3,
                    color: "#FFF",
                },
            }}
        >
            <DialogTitle>
                <Box display="flex" justifyContent="space-between">
                    {/* --- LEFT SIDE --- */}
                    <Box sx={{ display: "flex", flexDirection: "row", gap: 1 }}>
                        <Typography fontSize={14}>Keywords</Typography>
                        <Typography>•</Typography>

                        <Box sx={{ display: "flex", flexDirection: "row", gap: 0.5 }}>
                            <Box sx={{ position: "relative", top: -6 }}>
                                <FlagIcon
                                    isCircle={false}
                                    code={countriesJson
                                        .find((c) => c.name === row?.region_name)
                                        ?.code?.toLowerCase()}
                                    sx={{ width: 16, height: 16 }}
                                />
                            </Box>
                            <Typography
                                fontSize={14}
                                sx={{
                                    maxWidth: 280,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {row?.campaign_names}
                            </Typography>
                        </Box>

                        <Typography>•</Typography>

                        <Box component="span" sx={{ display: "inline-flex", alignItems: "center" }}>
                            <Box
                                sx={{
                                    borderRadius: "6px",
                                    background: "rgba(164, 111, 247, 0.19)",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    px: 1,
                                    py: 1,
                                    position: 'relative',
                                    top: -8,
                                    padding: "2px 8px"

                                }}
                            >
                                <Typography
                                    color="#CEA8F0"
                                    sx={{
                                        fontSize: 13,
                                        fontWeight: 600,
                                        lineHeight: 1.2,
                                        m: 0,
                                    }}
                                >
                                    KO Bidding Manual
                                </Typography>
                            </Box>
                        </Box>

                    </Box>


                    <Box display="flex" alignItems="center" gap={1.5}>
                        <TextField
                            label="Max CPC"
                            type="number"
                            variant="outlined"
                            size="small"
                            value={maxCpc}
                            onChange={(e) => setMaxCpc(e.target.value === "" ? "" : parseFloat(e.target.value))}
                            sx={{ width: 100 }}
                            InputProps={{
                                inputProps: { step: 0.01, style: { textAlign: "right" } },
                            }}
                        />

                        <Button
                            variant="contained"
                            onClick={handleApplyChanges}
                            sx={{
                                background: "#FFF",
                                color: "#111",
                                textTransform: "none",
                                fontWeight: 600,
                                fontSize: 13,
                                px: 2,
                                "&:hover": { background: "#E8E8E8" },
                            }}
                        >
                            Apply changes
                        </Button>

                        <Button
                            variant="outlined"
                            disabled
                            sx={{
                                borderColor: "#3C4653",
                                color: "#FFF",
                                textTransform: "none",
                                fontWeight: 600,
                                fontSize: 13,
                                px: 2,
                                "&:hover": {
                                    borderColor: "#5A6A7F",
                                    background: "rgba(255,255,255,0.05)",
                                },
                            }}
                        >
                            Enforce changes
                        </Button>
                    </Box>
                </Box>
            </DialogTitle>


            <DialogContent>
                <Box display="flex" justifyContent="space-between" gap={3}>
                    {[
                        {
                            title: "Smart Top",
                            data: row?.scatter_plot_top_blended_presence_rate,
                            yField: "top_blended_presence_rate",
                            color: "#42A5F5",
                            span: "MOST SAVINGS",
                            bgColorSpan: "#2D4555",
                            colorSpan: "#B6DDFF",
                        },
                        {
                            title: "Smart First",
                            data: row?.scatter_plot_abs_top_blended_presence_rate,
                            yField: "abs_top_blended_presence_rate",
                            color: "#66BB6A",
                        },
                        {
                            title: "Paid Top",
                            data: row?.scatter_plot_top_paid_presence_rate,
                            yField: "top_paid_presence_rate",
                            color: "#FFA726",
                        },
                        {
                            title: "Paid First",
                            data: row?.scatter_plot_abs_top_paid_presence_rate,
                            yField: "abs_top_paid_presence_rate",
                            color: "#AB47BC",
                            span: "MOST VISIBILITY",
                            bgColorSpan: "#49405E",
                            colorSpan: "#EBD6FD",
                        },
                    ].map((chart, i) => {
                        return (
                            <Box
                                key={i}
                                sx={{
                                    flex: 1,
                                    background: 'rgba(45, 50, 57, 0.90)',
                                    border: "1px solid #1E2A35",
                                    borderRadius: 3,
                                    p: 2.5,
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "space-between",
                                }}
                            >
                                <Box
                                    display="flex"
                                    justifyContent={chart.span ? "space-between" : "center"}
                                    alignItems="center"
                                    pb={2}
                                >
                                    <Typography fontWeight={600}>{chart.title}</Typography>

                                    {chart.span && (
                                        <Box
                                            sx={{
                                                bgcolor: chart?.bgColorSpan,
                                                color: chart?.colorSpan,
                                                px: 1.5,
                                                py: 1,
                                                borderRadius: 1,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                            }}
                                        >
                                            <Typography
                                                fontWeight={600}
                                                fontSize={13}
                                                sx={{ m: 0, lineHeight: 1 }}
                                            >
                                                {chart.span}
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>


                                <Box
                                    sx={{
                                        height: 250,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                    }}
                                >
                                    <CustomLineChart
                                        data={chart.data}
                                        yField={chart.yField}
                                        maxCpcValue={maxCpc}
                                    />
                                </Box>
                            </Box>
                        );
                    })}
                </Box>

                <Box pt={2} display={"flex"} justifyContent={"flex-end"} gap={2}>
                    <Box display={"flex"} flexDirection={"row"}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 16 16" fill="none">
                            <g filter="url(#filter0_d_2803_11729)">
                                <circle cx="8" cy="8" r="3" fill="#B7DCFB" />
                            </g>
                            <defs>
                                <filter id="filter0_d_2803_11729" x="0" y="0" width="22" height="22" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
                                    <feFlood flood-opacity="0" result="BackgroundImageFix" />
                                    <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
                                    <feMorphology radius="1" operator="dilate" in="SourceAlpha" result="effect1_dropShadow_2803_11729" />
                                    <feOffset />
                                    <feGaussianBlur stdDeviation="2" />
                                    <feComposite in2="hardAlpha" operator="out" />
                                    <feColorMatrix type="matrix" values="0 0 0 0 0.921569 0 0 0 0 0.839216 0 0 0 0 0.992157 0 0 0 0.2 0" />
                                    <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_2803_11729" />
                                    <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_2803_11729" result="shape" />
                                </filter>
                            </defs>
                        </svg>
                        <Typography>Date & Day</Typography>
                    </Box>
                    <Box display={"flex"} flexDirection={"row"}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 16 16" fill="none">
                            <g filter="url(#filter0_d_2803_11729)">
                                <circle cx="8" cy="8" r="3" fill="#B985F4" />
                            </g>
                            <defs>
                                <filter id="filter0_d_2803_11729" x="0" y="0" width="22" height="22" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
                                    <feFlood flood-opacity="0" result="BackgroundImageFix" />
                                    <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
                                    <feMorphology radius="1" operator="dilate" in="SourceAlpha" result="effect1_dropShadow_2803_11729" />
                                    <feOffset />
                                    <feGaussianBlur stdDeviation="2" />
                                    <feComposite in2="hardAlpha" operator="out" />
                                    <feColorMatrix type="matrix" values="0 0 0 0 0.921569 0 0 0 0 0.839216 0 0 0 0 0.992157 0 0 0 0.2 0" />
                                    <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_2803_11729" />
                                    <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_2803_11729" result="shape" />
                                </filter>
                            </defs>
                        </svg>
                        <Typography>Previous day (D-1)</Typography>
                    </Box>
                </Box>
            </DialogContent>


        </Dialog >
    );
}
